import { useCallback, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isCrmCoreRole } from '@crm/shared'
import {
  addComment,
  deleteComment,
  getComments,
  getItemMentionableUsers,
  getItemThread,
  updateComment,
} from '../../shared/api/comments.api'
import { apiErrorText } from '../../shared/api/errorText'
import type { Comment } from '../../shared/api/types'
import { toast } from '../../shared/ui/toastStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAuthStore } from '../auth/authStore'
import { CommentItem } from '../requests/components/CommentItem'
import { MentionInput, type MentionInputHandle } from '../requests/components/MentionInput'
import { ReplyHintBar } from '../requests/components/ReplyHintBar'
import {
  AttachImageButton,
  DraftImages,
  canSendComment,
  useCommentImages,
} from '../requests/components/commentImages'

/**
 * «Переписка по позиции» (с 21.10.2026): сюда ведут уведомления бухгалтеру,
 * складу и логисту, которых @упомянули в комментарии к позиции. Карточку заявки
 * они не открывают — видят только партномер и переписку этой позиции, без веток
 * квот (там цены поставщиков). Доступ проверяет сервер (comments.guest.ts).
 */
export function ItemThreadPage() {
  const { itemId = '' } = useParams<{ itemId: string }>()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [text, setText] = useState('')
  const [mentions, setMentions] = useState<string[]>([])
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const inputRef = useRef<MentionInputHandle>(null)
  const images = useCommentImages()
  const commentsKey = ['comments', 'item', itemId]

  const headerQuery = useQuery({
    queryKey: ['item-thread', itemId],
    queryFn: () => getItemThread(itemId),
    enabled: !!itemId,
  })
  const commentsQuery = useQuery({
    queryKey: commentsKey,
    queryFn: () => getComments({ requestItemId: itemId }),
    enabled: !!itemId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

  const refresh = () => void qc.invalidateQueries({ queryKey: commentsKey })

  const addMut = useMutation({
    mutationFn: () =>
      addComment({
        text,
        requestItemId: itemId,
        mentions,
        parentId: replyTo?.id ?? null,
        imageIds: images.ids,
      }),
    onSuccess: () => {
      setText('')
      images.clear()
      setMentions([])
      setReplyTo(null)
      refresh()
    },
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось отправить комментарий')),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, nextText }: { id: string; nextText: string }) =>
      updateComment(id, { text: nextText }),
    onSuccess: refresh,
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось изменить комментарий')),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: (_d, id) => {
      if (replyTo?.id === id) setReplyTo(null)
      refresh()
    },
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось удалить комментарий')),
  })

  const handleReply = useCallback((c: Comment) => {
    setReplyTo(c)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])
  const handleParentClick = useCallback((parentId: string) => {
    const el = document.querySelector<HTMLElement>(`[data-focus-id="${parentId}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])
  const loadUsers = useCallback(() => getItemMentionableUsers(itemId), [itemId])

  const canSend = canSendComment(text, images) && !addMut.isPending
  const submit = () => {
    if (canSend) addMut.mutate()
  }

  if (headerQuery.isLoading) return <PageLoader />
  if (headerQuery.isError) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl">
        <ErrorMessage error={headerQuery.error} />
      </div>
    )
  }
  const head = headerQuery.data
  const comments = commentsQuery.data ?? []

  return (
    <div className="p-4 sm:p-6 max-w-3xl space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-1">
        <p className="text-xs text-gray-500">Переписка по позиции</p>
        <h1 className="text-lg font-semibold text-gray-900 font-mono break-all">
          {head?.partNumber}
        </h1>
        {head && (
          <p className="text-sm text-gray-600">
            Заявка {head.requestNumber ?? '—'}
            {head.clientCode && <span className="font-mono"> · {head.clientCode}</span>}
            {' · '}
            {head.brand || 'производитель не указан'}
            {' · '}
            {head.quantity.toLocaleString('ru-RU')} шт.
            {' · '}
            Менеджер: {head.managerName ?? '—'}
          </p>
        )}
        {user && isCrmCoreRole(user.role) && (
          <Link
            to={`/request-items/${itemId}`}
            className="inline-block pt-1 text-sm text-brand-600 hover:text-brand-700"
          >
            Открыть позицию →
          </Link>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        {commentsQuery.isLoading ? (
          <PageLoader />
        ) : commentsQuery.isError ? (
          <ErrorMessage error={commentsQuery.error} />
        ) : comments.length === 0 ? (
          <p className="text-xs text-gray-400">Комментариев пока нет.</p>
        ) : (
          <div className="space-y-2">
            {comments.map((c) => (
              <CommentItem
                key={c.id}
                comment={c}
                currentUserId={user?.id}
                onReply={handleReply}
                onSubmitEdit={async (id, nextText) => {
                  await updateMut.mutateAsync({ id, nextText })
                }}
                onDelete={async (id) => {
                  await deleteMut.mutateAsync(id)
                }}
                onParentClick={handleParentClick}
              />
            ))}
          </div>
        )}

        <div className="border-t border-gray-100 pt-3 space-y-1">
          {replyTo && <ReplyHintBar parent={replyTo} onClear={() => setReplyTo(null)} />}
          <DraftImages state={images} />
          <div className="flex items-end gap-2">
            <MentionInput
              ref={inputRef}
              value={text}
              onChange={(t, m) => {
                setText(t)
                setMentions(m)
              }}
              onSubmit={submit}
              onPasteFiles={images.addFiles}
              allowEmptySubmit={canSend}
              requestId={undefined}
              itemId={itemId}
              loadUsers={loadUsers}
              placeholder="Комментарий… (@ — упомянуть; Enter — отправить)"
              disabled={addMut.isPending}
            />
            <AttachImageButton state={images} disabled={addMut.isPending} />
            <button
              onClick={submit}
              disabled={!canSend}
              aria-label="Отправить комментарий"
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
